import { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import countryList from "react-select-country-list";
import { vendorApi } from "../../services/vendorApi";

const DEFAULT_COUNTRY_CODE = "GB";

const initialForm = {
  vendorCode: "",
  name: "",
  contactPerson: "",
  email: "",
  phone: "",
  city: "",
  address: "",
  services: "",
  rating: "",
  spend: "",
  status: "active",
  notes: "",
};

export default function VendorFormModal({ open, onClose, onSaved, vendor }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [countryOption, setCountryOption] = useState(null);
  const [isDark, setIsDark] = useState(false);

  const countryOptions = useMemo(() => countryList().getData(), []);

  const defaultCountry = useMemo(() => {
    return (
      countryOptions.find((item) => item.value === DEFAULT_COUNTRY_CODE) || null
    );
  }, [countryOptions]);

  useEffect(() => {
    if (!open) return;

    const syncTheme = () => {
      if (typeof document === "undefined") return;
      setIsDark(document.documentElement.classList.contains("dark"));
    };

    syncTheme();

    const observer =
      typeof MutationObserver !== "undefined" && typeof document !== "undefined"
        ? new MutationObserver(syncTheme)
        : null;

    if (observer) {
      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ["class"],
      });
    }

    return () => observer?.disconnect();
  }, [open]);

  useEffect(() => {
    if (!open) return;

    if (vendor) {
      const matchedCountry =
        countryOptions.find(
          (item) =>
            item.value === vendor.country ||
            item.label === vendor.country ||
            item.value === String(vendor.country).toUpperCase()
        ) || defaultCountry;

      setCountryOption(matchedCountry);

      setForm({
        vendorCode: vendor.vendorCode || "",
        name: vendor.name || "",
        contactPerson: vendor.contactPerson || "",
        email: vendor.email || "",
        phone: vendor.phone || "",
        city: vendor.city || "",
        address: vendor.address || "",
        services: Array.isArray(vendor.services)
          ? vendor.services.join(", ")
          : typeof vendor.services === "string"
          ? vendor.services
          : "",
        rating:
          vendor.rating !== null && vendor.rating !== undefined
            ? String(vendor.rating)
            : "",
        spend:
          vendor.spend !== null && vendor.spend !== undefined
            ? String(vendor.spend)
            : "",
        status: vendor.status || "active",
        notes: vendor.notes || "",
      });
    } else {
      setForm(initialForm);
      setCountryOption(defaultCountry);
    }
  }, [open, vendor, countryOptions, defaultCountry]);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCountryChange = (selected) => {
    setCountryOption(selected || null);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = {
        vendorCode: form.vendorCode.trim() || undefined,
        name: form.name.trim(),
        contactPerson: form.contactPerson.trim() || undefined,
        email: form.email.trim() || undefined,
        phone: form.phone.trim() || undefined,
        country: countryOption?.label || countryOption?.value || undefined,
        city: form.city.trim() || undefined,
        address: form.address.trim() || undefined,
        services: form.services.trim(),
        rating: form.rating === "" ? 0 : Number(form.rating),
        spend: form.spend === "" ? 0 : Number(form.spend),
        status: form.status,
        notes: form.notes.trim() || undefined,
      };

      const res = vendor
        ? await vendorApi.update(vendor.id, payload)
        : await vendorApi.create(payload);

      onSaved?.(res.data?.message || "Vendor saved successfully");
      onClose();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to save vendor");
    } finally {
      setSaving(false);
    }
  };

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: "48px",
      borderRadius: "16px",
      borderColor: isDark ? "#334155" : state.isFocused ? "#3b82f6" : "#e2e8f0",
      backgroundColor: isDark ? "#020617" : "#ffffff",
      color: isDark ? "#e2e8f0" : "#0f172a",
      boxShadow: state.isFocused
        ? `0 0 0 2px ${isDark ? "rgba(59, 130, 246, 0.18)" : "rgba(59, 130, 246, 0.12)"}`
        : "none",
      "&:hover": {
        borderColor: isDark ? "#3b82f6" : "#3b82f6",
      },
    }),
    input: (base) => ({
      ...base,
      color: isDark ? "#e2e8f0" : "#0f172a",
    }),
    singleValue: (base) => ({
      ...base,
      color: isDark ? "#e2e8f0" : "#0f172a",
    }),
    placeholder: (base) => ({
      ...base,
      color: isDark ? "#64748b" : "#94a3b8",
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: isDark ? "#020617" : "#ffffff",
      color: isDark ? "#e2e8f0" : "#0f172a",
      border: isDark ? "1px solid #334155" : "1px solid #e2e8f0",
      borderRadius: "16px",
      overflow: "hidden",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? (isDark ? "#1d4ed8" : "#eff6ff")
        : state.isFocused
        ? (isDark ? "#1e293b" : "#f8fafc")
        : isDark
        ? "#020617"
        : "#ffffff",
      color: state.isSelected
        ? (isDark ? "#ffffff" : "#1d4ed8")
        : isDark
        ? "#e2e8f0"
        : "#0f172a",
      cursor: "pointer",
    }),
    valueContainer: (base) => ({
      ...base,
      paddingLeft: "14px",
      paddingRight: "14px",
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
  };

  if (!open) return null;

  const baseInputClass =
    "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-500";
  const labelClass = "mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300";

  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/40 px-4 py-6 backdrop-blur-[2px]">
      <div className="max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-3xl border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900">
        <div className="border-b border-slate-100 px-6 py-5 dark:border-slate-800">
          <h2 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">
            {vendor ? "Edit Vendor" : "New Vendor"}
          </h2>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Create or update supplier details for invoices.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-6 p-6">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className={labelClass}>Vendor Code</label>
              <input
                value={form.vendorCode}
                onChange={handleChange("vendorCode")}
                placeholder="Leave blank to auto-generate"
                className={baseInputClass}
              />
            </div>

            <div>
              <label className={labelClass}>Vendor Name *</label>
              <input
                value={form.name}
                onChange={handleChange("name")}
                placeholder="Enter vendor name"
                required
                className={baseInputClass}
              />
            </div>

            <div>
              <label className={labelClass}>Contact Person</label>
              <input
                value={form.contactPerson}
                onChange={handleChange("contactPerson")}
                placeholder="Contact person name"
                className={baseInputClass}
              />
            </div>

            <div>
              <label className={labelClass}>Email</label>
              <input
                value={form.email}
                onChange={handleChange("email")}
                placeholder="vendor@example.com"
                type="email"
                className={baseInputClass}
              />
            </div>

            <div>
              <label className={labelClass}>Phone</label>
              <input
                value={form.phone}
                onChange={handleChange("phone")}
                placeholder="+92..."
                className={baseInputClass}
              />
            </div>

            <div>
              <label className={labelClass}>Country</label>
              <Select
                options={countryOptions}
                value={countryOption}
                onChange={handleCountryChange}
                placeholder="Select country"
                isClearable
                isSearchable
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                menuPosition="fixed"
                styles={selectStyles}
              />
            </div>

            <div>
              <label className={labelClass}>City</label>
              <input
                value={form.city}
                onChange={handleChange("city")}
                placeholder="Enter city"
                className={baseInputClass}
              />
            </div>

            <div>
              <label className={labelClass}>Rating</label>
              <input
                value={form.rating}
                onChange={handleChange("rating")}
                type="number"
                step="0.1"
                min="0"
                max="5"
                placeholder="0.0 to 5.0"
                className={baseInputClass}
              />
            </div>

            <div>
              <label className={labelClass}>Spend</label>
              <input
                value={form.spend}
                onChange={handleChange("spend")}
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                className={baseInputClass}
              />
            </div>

            <div>
              <label className={labelClass}>Status</label>
              <select
                value={form.status}
                onChange={handleChange("status")}
                className={baseInputClass}
              >
                <option value="active">Active</option>
                <option value="pending_review">Pending Review</option>
                <option value="high_risk">High Risk</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div>
              <label className={labelClass}>Services</label>
              <input
                value={form.services}
                onChange={handleChange("services")}
                placeholder="Air freight, customs clearance, warehousing"
                className={baseInputClass}
              />
            </div>

            <div className="md:col-span-2">
              <label className={labelClass}>Address</label>
              <textarea
                value={form.address}
                onChange={handleChange("address")}
                rows="3"
                placeholder="Full address"
                className={baseInputClass}
              />
            </div>

            <div className="md:col-span-2">
              <label className={labelClass}>Notes</label>
              <textarea
                value={form.notes}
                onChange={handleChange("notes")}
                rows="3"
                placeholder="Optional notes"
                className={baseInputClass}
              />
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 border-t border-slate-100 pt-4 dark:border-slate-800">
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Country list is loaded from the package data, and new vendors default to the United Kingdom.
            </p>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-medium text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={saving}
                className="rounded-2xl bg-blue-600 px-6 py-3 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {saving ? "Saving..." : vendor ? "Update Vendor" : "Create Vendor"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}